import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../utils/logger';
import { ProductBatch } from '../utils/product-chunker';

// Constants
const BATCH_SESSION_STORE_KEY = 'pms_batch_sessions';
const SESSION_EXPIRY_HOURS = 24;

/**
 * Represents a failed batch with error information
 */
export interface FailedBatch {
  batchIndex: number;
  error: string;
  retryCount: number;
  lastAttempt: number;
}

/**
 * Represents a completed batch
 */
export interface CompletedBatch {
  batchIndex: number;
  batchId: string;       // Server-assigned batch ID
  productCount: number;
  completedAt: number;
}

/**
 * Represents a batch upload session stored in IndexedDB
 */
export interface BatchSession {
  sessionId: string;
  serverSessionId?: string;     // Session ID from backend (if created)
  totalBatches: number;
  totalProducts: number;
  completedBatches: CompletedBatch[];
  failedBatches: FailedBatch[];
  pendingBatchIndices: number[];
  status: 'active' | 'paused' | 'completed' | 'partial_failure';
  createdAt: number;
  updatedAt: number;

  // Store batch metadata for resume capability
  batchesMetadata: BatchMetadata[];
}

/**
 * Lightweight metadata for a batch (doesn't store actual File objects)
 */
export interface BatchMetadata {
  index: number;
  productIds: string[];
  plankGroupIds: string[];
  estimatedSizeMB: number;
  productCount: number;
}

/**
 * Progress information for the current session
 */
export interface BatchUploadProgress {
  sessionId: string;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
  currentBatchIndex: number | null;
  totalProducts: number;
  completedProducts: number;
  status: 'idle' | 'uploading' | 'polling' | 'paused' | 'completed' | 'error';
  estimatedRemainingBatches: number;
}

/**
 * Manages batch upload sessions with IndexedDB persistence
 */
class BatchSessionManagerClass {
  private currentSession: BatchSession | null = null;

  /**
   * Creates a new batch session
   */
  async createSession(batches: ProductBatch[]): Promise<BatchSession> {
    const sessionId = uuidv4();
    const now = Date.now();

    const totalProducts = batches.reduce((sum, b) => sum + b.products.length, 0);

    const session: BatchSession = {
      sessionId,
      totalBatches: batches.length,
      totalProducts,
      completedBatches: [],
      failedBatches: [],
      pendingBatchIndices: batches.map(b => b.index),
      status: 'active',
      createdAt: now,
      updatedAt: now,
      batchesMetadata: batches.map(b => ({
        index: b.index,
        productIds: b.productIds,
        plankGroupIds: b.plankGroupIds,
        estimatedSizeMB: b.estimatedSizeMB,
        productCount: b.products.length
      }))
    };

    await this.saveSession(session);
    this.currentSession = session;

    logger.log('[BatchSessionManager] Created new session', {
      sessionId,
      totalBatches: batches.length,
      totalProducts
    });

    return session;
  }

  /**
   * Updates the server session ID after backend creates it
   */
  async setServerSessionId(sessionId: string, serverSessionId: string): Promise<void> {
    const session = await this.getSession(sessionId);
    if (session) {
      session.serverSessionId = serverSessionId;
      session.updatedAt = Date.now();
      await this.saveSession(session);
    }
  }

  /**
   * Gets a session by ID
   */
  async getSession(sessionId: string): Promise<BatchSession | null> {
    try {
      const sessions = await this.getAllSessions();
      return sessions.find(s => s.sessionId === sessionId) || null;
    } catch (error) {
      logger.error('[BatchSessionManager] Error getting session:', error);
      return null;
    }
  }

  /**
   * Gets all stored sessions
   */
  async getAllSessions(): Promise<BatchSession[]> {
    try {
      const sessions = await localforage.getItem<BatchSession[]>(BATCH_SESSION_STORE_KEY);
      return sessions || [];
    } catch (error) {
      logger.error('[BatchSessionManager] Error getting all sessions:', error);
      return [];
    }
  }

  /**
   * Saves a session to IndexedDB
   */
  private async saveSession(session: BatchSession): Promise<void> {
    try {
      const sessions = await this.getAllSessions();
      const index = sessions.findIndex(s => s.sessionId === session.sessionId);

      if (index >= 0) {
        sessions[index] = session;
      } else {
        sessions.push(session);
      }

      await localforage.setItem(BATCH_SESSION_STORE_KEY, sessions);
      this.currentSession = session;
    } catch (error) {
      logger.error('[BatchSessionManager] Error saving session:', error);
      throw error;
    }
  }

  /**
   * Marks a batch as completed
   */
  async markBatchCompleted(
    sessionId: string,
    batchIndex: number,
    batchId: string,
    productCount: number
  ): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) {
      logger.error('[BatchSessionManager] Session not found:', sessionId);
      return;
    }

    // Add to completed batches
    session.completedBatches.push({
      batchIndex,
      batchId,
      productCount,
      completedAt: Date.now()
    });

    // Remove from pending
    session.pendingBatchIndices = session.pendingBatchIndices.filter(i => i !== batchIndex);

    // Remove from failed if it was there (retry succeeded)
    session.failedBatches = session.failedBatches.filter(f => f.batchIndex !== batchIndex);

    // Update status if all batches are done
    if (session.pendingBatchIndices.length === 0 && session.failedBatches.length === 0) {
      session.status = 'completed';
    } else if (session.pendingBatchIndices.length === 0 && session.failedBatches.length > 0) {
      session.status = 'partial_failure';
    }

    session.updatedAt = Date.now();
    await this.saveSession(session);

    logger.log('[BatchSessionManager] Batch completed', {
      sessionId,
      batchIndex,
      batchId,
      remainingBatches: session.pendingBatchIndices.length
    });
  }

  /**
   * Marks a batch as failed
   */
  async markBatchFailed(
    sessionId: string,
    batchIndex: number,
    error: string
  ): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) {
      logger.error('[BatchSessionManager] Session not found:', sessionId);
      return;
    }

    // Find existing failed batch entry or create new
    const existingFailed = session.failedBatches.find(f => f.batchIndex === batchIndex);
    if (existingFailed) {
      existingFailed.error = error;
      existingFailed.retryCount += 1;
      existingFailed.lastAttempt = Date.now();
    } else {
      session.failedBatches.push({
        batchIndex,
        error,
        retryCount: 1,
        lastAttempt: Date.now()
      });
    }

    // Remove from pending
    session.pendingBatchIndices = session.pendingBatchIndices.filter(i => i !== batchIndex);

    // Update status
    if (session.pendingBatchIndices.length === 0) {
      session.status = 'partial_failure';
    }

    session.updatedAt = Date.now();
    await this.saveSession(session);

    logger.log('[BatchSessionManager] Batch failed', {
      sessionId,
      batchIndex,
      error,
      remainingBatches: session.pendingBatchIndices.length
    });
  }

  /**
   * Marks failed batches as pending for retry
   */
  async markFailedBatchesForRetry(sessionId: string): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) return;

    // Move failed batches back to pending
    const failedIndices = session.failedBatches.map(f => f.batchIndex);
    session.pendingBatchIndices = [...session.pendingBatchIndices, ...failedIndices].sort((a, b) => a - b);
    session.failedBatches = [];
    session.status = 'active';
    session.updatedAt = Date.now();

    await this.saveSession(session);

    logger.log('[BatchSessionManager] Failed batches marked for retry', {
      sessionId,
      retriedBatches: failedIndices
    });
  }

  /**
   * Pauses the current session
   */
  async pauseSession(sessionId: string): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) return;

    session.status = 'paused';
    session.updatedAt = Date.now();
    await this.saveSession(session);

    logger.log('[BatchSessionManager] Session paused', { sessionId });
  }

  /**
   * Resumes a paused session
   */
  async resumeSession(sessionId: string): Promise<void> {
    const session = await this.getSession(sessionId);
    if (!session) return;

    session.status = 'active';
    session.updatedAt = Date.now();
    await this.saveSession(session);

    logger.log('[BatchSessionManager] Session resumed', { sessionId });
  }

  /**
   * Gets a resumable session if one exists
   * Returns the most recent active or paused session that hasn't expired
   */
  async getResumableSession(): Promise<BatchSession | null> {
    try {
      const sessions = await this.getAllSessions();
      const now = Date.now();
      const expiryThreshold = now - (SESSION_EXPIRY_HOURS * 60 * 60 * 1000);

      // Find active or paused sessions that haven't expired
      const resumableSessions = sessions
        .filter(s =>
          (s.status === 'active' || s.status === 'paused' || s.status === 'partial_failure') &&
          s.createdAt > expiryThreshold &&
          s.pendingBatchIndices.length > 0
        )
        .sort((a, b) => b.updatedAt - a.updatedAt);

      if (resumableSessions.length > 0) {
        const session = resumableSessions[0];
        logger.log('[BatchSessionManager] Found resumable session', {
          sessionId: session.sessionId,
          completedBatches: session.completedBatches.length,
          pendingBatches: session.pendingBatchIndices.length,
          failedBatches: session.failedBatches.length
        });
        return session;
      }

      return null;
    } catch (error) {
      logger.error('[BatchSessionManager] Error getting resumable session:', error);
      return null;
    }
  }

  /**
   * Gets the current progress for a session
   */
  async getProgress(sessionId: string): Promise<BatchUploadProgress | null> {
    const session = await this.getSession(sessionId);
    if (!session) return null;

    const completedProducts = session.completedBatches.reduce(
      (sum, b) => sum + b.productCount,
      0
    );

    return {
      sessionId: session.sessionId,
      totalBatches: session.totalBatches,
      completedBatches: session.completedBatches.length,
      failedBatches: session.failedBatches.length,
      currentBatchIndex: session.pendingBatchIndices[0] ?? null,
      totalProducts: session.totalProducts,
      completedProducts,
      status: session.status === 'active' ? 'uploading' : session.status === 'partial_failure' ? 'error' : session.status,
      estimatedRemainingBatches: session.pendingBatchIndices.length
    };
  }

  /**
   * Clears a completed or cancelled session
   */
  async clearSession(sessionId: string): Promise<void> {
    try {
      const sessions = await this.getAllSessions();
      const filtered = sessions.filter(s => s.sessionId !== sessionId);
      await localforage.setItem(BATCH_SESSION_STORE_KEY, filtered);

      if (this.currentSession?.sessionId === sessionId) {
        this.currentSession = null;
      }

      logger.log('[BatchSessionManager] Session cleared', { sessionId });
    } catch (error) {
      logger.error('[BatchSessionManager] Error clearing session:', error);
    }
  }

  /**
   * Cleans up expired sessions
   */
  async cleanupExpiredSessions(): Promise<void> {
    try {
      const sessions = await this.getAllSessions();
      const now = Date.now();
      const expiryThreshold = now - (SESSION_EXPIRY_HOURS * 60 * 60 * 1000);

      const validSessions = sessions.filter(s =>
        s.createdAt > expiryThreshold ||
        s.status === 'active' ||
        s.status === 'paused'
      );

      if (validSessions.length !== sessions.length) {
        await localforage.setItem(BATCH_SESSION_STORE_KEY, validSessions);
        logger.log('[BatchSessionManager] Cleaned up expired sessions', {
          removed: sessions.length - validSessions.length
        });
      }
    } catch (error) {
      logger.error('[BatchSessionManager] Error cleaning up sessions:', error);
    }
  }

  /**
   * Gets the current in-memory session
   */
  getCurrentSession(): BatchSession | null {
    return this.currentSession;
  }

  /**
   * Sets the current session (for resume)
   */
  setCurrentSession(session: BatchSession | null): void {
    this.currentSession = session;
  }
}

// Export singleton instance
export const BatchSessionManager = new BatchSessionManagerClass();
